import React, { ChangeEvent } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faUsers } from '@fortawesome/pro-light-svg-icons'

import { InputSearch, UserProfileImage } from '@domain-group/suit-theme'

import { useDebounce, useSetState } from '@domain-group/react-hooks'

import type { useAgentsPayload } from '@hooks/useAgents'
import type { useTeamsPayload } from '@hooks/useTeams'

import useAgents from '@hooks/useAgents'
import useSelector from '@hooks/useSelector'
import useTeamMembers from '@hooks/useTeamMembers'
import useTeams from '@hooks/useTeams'

import type { AgentModel, TeamModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

type FilterUnselectedEntities = {
  entities: useAgentsPayload['filteredAgents'] | useTeamsPayload['filteredTeams'],
  selectedIds: number[],
}

const filterUnselectedEntities = (params: FilterUnselectedEntities) => {
  const { entities, selectedIds } = params
  return entities.filter(entity => !selectedIds.includes(entity.id))
}

export type agentOrTeamObject = {
  iconLeft: React.ReactNode,
  key: string,
  text: string,
}

type MapResultsParams = {
  agents: AgentModel[],
  images: EntitiesState['images'],
  teams: TeamModel[],
  users: EntitiesState['users'],
}

const mapResults = (params: MapResultsParams) => {
  const {
    agents, images, teams, users,
  } = params

  return {
    groups: [
      {
        key: 'teams',
        title: 'TEAMS',
        items: teams.map((team) => {
          const { cover_image_id } = team
          const image = cover_image_id && images[cover_image_id] ? images[cover_image_id] : {}
          return {
            // @ts-expect-error TS2339
            iconLeft: <UserProfileImage imageUrl={image?.thumb_url}><FontAwesomeIcon icon={faUsers} /></UserProfileImage>,
            text: team.title,
            key: `Team-${team.id}`,
          }
        }),
      },
      {
        key: 'agents',
        title: 'AGENTS',
        items: agents.map((agent) => {
          // @ts-expect-error TS2538
          const user = users[agent.user_id] || {}
          const image = user.image_id && images[user.image_id] ? images[user.image_id] : {}
          return {
            // @ts-expect-error TS2339
            iconLeft: <UserProfileImage imageUrl={image?.thumb_url}><FontAwesomeIcon icon={faUser} /></UserProfileImage>,
            text: agent.full_name,
            key: `Agent-${agent.id}`,
          }
        }),
      },
    ],
  }
}

const defaultState = {
  filterString: '',
}

interface AgentOrTeamSearchProps {
  callbacks: {
    selectAgentOrTeam: (agentOrTeam: agentOrTeamObject) => void,
  },
  selectedAgentIds: number[],
  selectedTeamId: number,
}

const AgentOrTeamSearch = (props: AgentOrTeamSearchProps) => {
  const {
    callbacks, selectedAgentIds, selectedTeamId,
  } = props
  const { selectAgentOrTeam } = callbacks

  const [state, setState] = useSetState(defaultState)
  const { filterString } = state

  const debouncedString = useDebounce(filterString, 150)

  // Find teamMembers so they can be excluded from filteredAgents
  const { filteredTeamMemberUserIds } = useTeamMembers({ filters: { team_id: selectedTeamId } })
  const teamMemberUserIds = selectedTeamId ? filteredTeamMemberUserIds : []

  const { filteredAgents } = useAgents({
    filters: {
      exclude_user_ids: teamMemberUserIds,
      string: debouncedString,
    },
    performHttpRequests: debouncedString.length,
    requestOptions: {
      include_agent_user: true,
      include_image_sizes: true,
      include_user_image: true,
    },
  })

  const { filteredTeams } = useTeams({
    filters: {
      string: debouncedString,
    },
    performHttpRequests: debouncedString.length,
    requestOptions: {
      include_image_sizes: true,
      include_team_agent_members: true,
      include_team_cover_image: true,
      include_team_member_user: true,
      include_team_members: true,
      include_user_image: true,
    },
  })

  const { images, users } = useSelector(reduxState => reduxState.entities)

  const results = mapResults({
    // @ts-expect-error TS2322
    agents: filterUnselectedEntities({ entities: filteredAgents, selectedIds: selectedAgentIds }),
    images,
    // @ts-expect-error TS2322
    teams: filterUnselectedEntities({ entities: filteredTeams, selectedIds: [selectedTeamId] }),
    users,
  })

  return (
    <InputSearch
      callbacks={{
        // @ts-expect-error TS2322
        selectItem: (item: agentOrTeamObject) => selectAgentOrTeam(item),
      }}
      onChange={(e: ChangeEvent<HTMLInputElement>) => setState({ filterString: e.target.value })}
      results={results}
      placeholder="Search by name or title"
      value={filterString}
    />
  )
}

export default AgentOrTeamSearch
